import { signal, computed, Signal, WritableSignal } from '@angular/core';
import {
  TileProcessingItem,
  TileProcessingStatus,
  BatchProcessingConfig,
  DEFAULT_BATCH_CONFIG,
  ProcessingStatistics,
  ProcessingState,
  TILE_PROCESSING_PROGRESS_WEIGHT
} from '../models/csv-tile';

/**
 * Base service for batch processing operations
 * Provides common infrastructure for processing tiles in batches
 */
export abstract class BaseBatchProcessorService {
  protected config: BatchProcessingConfig = DEFAULT_BATCH_CONFIG;

  readonly processingState: WritableSignal<ProcessingState> = signal<ProcessingState>(ProcessingState.IDLE);
  readonly processingItems: WritableSignal<TileProcessingItem[]> = signal<TileProcessingItem[]>([]);
  readonly statistics: WritableSignal<ProcessingStatistics> = signal<ProcessingStatistics>({
    total: 0,
    queued: 0,
    processing: 0,
    created: 0,
    errors: 0,
    warnings: 0,
    progressPercentage: 0
  });

  readonly isProcessing: Signal<boolean> = computed(() => this.processingState() === ProcessingState.PROCESSING);
  readonly isPaused: Signal<boolean> = computed(() => this.processingState() === ProcessingState.PAUSED);
  readonly isCompleted: Signal<boolean> = computed(() => this.processingState() === ProcessingState.COMPLETED);

  pauseProcessing(): void {
    if (this.processingState() === ProcessingState.PROCESSING) {
      this.processingState.set(ProcessingState.PAUSED);
    }
  }

  resumeProcessing(): void {
    // Allow resuming from both PAUSED and COMPLETED states
    if (this.processingState() !== ProcessingState.PAUSED &&
        this.processingState() !== ProcessingState.COMPLETED) {
      return;
    }

    // Get items that are not completed yet (queued or stuck in processing)
    const remainingItems = this.processingItems().filter(item => {
      return item.status === TileProcessingStatus.QUEUED ||
             item.status === TileProcessingStatus.PROCESSING ||
             item.status === TileProcessingStatus.LINKING_APPS ||
             item.status === TileProcessingStatus.LINKING_TAGS ||
             item.status === TileProcessingStatus.AWAITING_FOLDER_LINKING ||
             item.status === TileProcessingStatus.LINKING_SUPER_TILES;
    });

    if (remainingItems.length === 0) {
      // No items left to process - set state to completed
      this.processingState.set(ProcessingState.COMPLETED);
      return;
    }

    // Reset items that were stuck in processing back to QUEUED using proper signal update
    remainingItems.forEach(item => {
      if (item.status !== TileProcessingStatus.QUEUED) {
        this.updateStatus(item.id, TileProcessingStatus.QUEUED);
      }
    });

    // Now set state to PROCESSING and restart
    this.processingState.set(ProcessingState.PROCESSING);
    this.restartProcessing(remainingItems);
  }

  protected abstract restartProcessing(items: TileProcessingItem[]): void;

  stopProcessing(): void {
    this.processingState.set(ProcessingState.COMPLETED);
  }

  protected updateStatus(itemId: string, status: TileProcessingStatus, error?: string, tileId?: string): void {
    this.processingItems.update(items => {
      const item = items.find(i => i.id === itemId);
      if (item) {
        item.status = status;
        if (error !== undefined) {
          item.error = error;
        }
        if (tileId) {
          item.tileId = tileId;
        }
      }
      return [...items];
    });
    this.statistics.set(this.calculateStatistics(this.processingItems()));
  }

  protected calculateStatistics(items: TileProcessingItem[]): ProcessingStatistics {
    const stats: ProcessingStatistics = {
      total: items.length,
      queued: 0,
      processing: 0,
      created: 0,
      errors: 0,
      warnings: 0,
      progressPercentage: 0
    };

    let completed = 0;

    items.forEach(item => {
      switch(item.status) {
        case TileProcessingStatus.QUEUED:
          stats.queued++;
          break;
        case TileProcessingStatus.PROCESSING:
        case TileProcessingStatus.LINKING_APPS:
        case TileProcessingStatus.LINKING_TAGS:
        case TileProcessingStatus.AWAITING_FOLDER_LINKING:
        case TileProcessingStatus.LINKING_SUPER_TILES:
          stats.processing++;
          completed += TILE_PROCESSING_PROGRESS_WEIGHT[item.status]!;
          break;
        case TileProcessingStatus.CREATED:
        case TileProcessingStatus.UPDATED:
          stats.created++;
          completed++;
          break;
        case TileProcessingStatus.ERROR_PERMISSIONS:
        case TileProcessingStatus.ERROR_GENERAL:
          stats.errors++;
          completed++;
          break;
        case TileProcessingStatus.ERROR_APP_LINKING:
        case TileProcessingStatus.ERROR_FOLDER_LINKING:
        case TileProcessingStatus.ERROR_LINKING:
          stats.errors++;
          stats.created++;
          completed++;
          break;
        case TileProcessingStatus.ERROR_TAG_LINKING:
        case TileProcessingStatus.WARNING_PARTIAL:
          stats.warnings++;
          stats.created++;
          completed++;
          break;
      }
    });

    stats.progressPercentage = stats.total > 0
      ? Math.round((completed / stats.total) * 100)
      : 0;

    return stats;
  }

  protected initializeProcessing(items: TileProcessingItem[]): void {
    this.processingItems.set(items);
    this.statistics.set({
      total: items.length,
      queued: items.length,
      processing: 0,
      created: 0,
      errors: 0,
      warnings: 0,
      progressPercentage: 0
    });
    this.processingState.set(ProcessingState.PROCESSING);
  }
}
